"use client";

import { useTheme } from "next-themes";
import { useEffect } from "react";

/**
 * Dynamically swaps the favicon based on the app's current theme.
 *
 * Light mode → #2167AD (blue) favicon
 * Dark mode  → #00FFFF (cyan) favicon
 *
 * These two colours are deliberately still literals, and the SVGs they live in
 * are not generated from tokens: an icon cannot switch per design variant (it is
 * one file, fetched and cached by the browser before any theme attribute
 * exists), and favicons are a one-way door through caches. They change once,
 * when ratification picks the winning variant — see ADR-097.
 */
export function FaviconSwitcher() {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (!resolvedTheme) {
      return;
    }

    const faviconHref =
      resolvedTheme === "dark" ? "/favicon-dark.svg" : "/favicon-light.svg";

    let link = document.querySelector<HTMLLinkElement>(
      'link[rel="icon"][data-dynamic="true"]'
    );

    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/svg+xml";
      link.setAttribute("data-dynamic", "true");
      document.head.appendChild(link);
    }

    // Every icon link, not only ours: the ones the document ships with carry
    // `media` queries that would otherwise keep a browser on the icon for the
    // theme the OS prefers rather than the one the app is showing. The link
    // above is in this list.
    for (const candidate of document.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"]'
    )) {
      candidate.removeAttribute("media");
      candidate.href = faviconHref;
    }
  }, [resolvedTheme]);

  return null;
}
